/**
 * Text-to-Speech API - Web Speech API wrapper
 * Factory function following the app's architectural patterns
 */

export interface TTSVoiceConfig {
  readonly name?: string;
  readonly lang?: string;
  readonly gender?: "male" | "female";
}

export interface TTSIntonationConfig {
  readonly rate?: number; // Speed: 0.1 to 10 (default: 1)
  readonly pitch?: number; // Pitch: 0 to 2 (default: 1)
  readonly volume?: number; // Volume: 0 to 1 (default: 1)
}

export interface TTSConfig {
  readonly voice?: TTSVoiceConfig;
  readonly intonation?: TTSIntonationConfig;
  readonly onStart?: () => void;
  readonly onEnd?: () => void;
  readonly onError?: (error: string) => void;
}

export interface TTSMessage {
  readonly text: string;
  readonly intonation?: TTSIntonationConfig;
  readonly voice?: TTSVoiceConfig;
}

export interface TTSAPI {
  readonly speak: (message: TTSMessage | string) => void;
  readonly stop: () => void;
  readonly pause: () => void;
  readonly resume: () => void;
  readonly isSpeaking: () => boolean;
  readonly isPaused: () => boolean;
  readonly getVoices: () => SpeechSynthesisVoice[];
  readonly setDefaultConfig: (config: TTSConfig) => void;
  readonly destroy: () => void;
}

/**
 * Creates a TTS API instance with Web Speech API
 * @param config - Default configuration for TTS
 * @returns TTS API
 */
export const createTTSAPI = (config: TTSConfig = {}): TTSAPI => {
  let isDestroyed = false;
  let defaultConfig = { ...config };
  let currentUtterance: SpeechSynthesisUtterance | null = null;

  // Check for Web Speech API support
  if (!("speechSynthesis" in window)) {
    console.warn("Text-to-Speech not supported in this browser");

    // Return no-op API for unsupported browsers
    return {
      speak: () => console.warn("TTS not supported"),
      stop: () => {},
      pause: () => {},
      resume: () => {},
      isSpeaking: () => false,
      isPaused: () => false,
      getVoices: () => [],
      setDefaultConfig: () => {},
      destroy: () => {},
    };
  }

  /**
   * Find best matching voice based on config
   */
  const findVoice = (
    voiceConfig?: TTSVoiceConfig
  ): SpeechSynthesisVoice | null => {
    if (!voiceConfig) return null;

    const voices = speechSynthesis.getVoices();

    // Try exact name match first
    if (voiceConfig.name) {
      const exactMatch = voices.find(
        (voice) => voice.name === voiceConfig.name
      );
      if (exactMatch) return exactMatch;
    }

    // Try language match
    if (voiceConfig.lang) {
      const langMatch = voices.find((voice) =>
        voice.lang.startsWith(voiceConfig.lang!)
      );
      if (langMatch) return langMatch;
    }

    // Try gender match (this is heuristic, as there's no direct gender property)
    if (voiceConfig.gender) {
      const genderMatch = voices.find((voice) => {
        const name = voice.name.toLowerCase();
        if (voiceConfig.gender === "female") {
          return (
            name.includes("female") ||
            name.includes("woman") ||
            name.includes("girl")
          );
        } else {
          return (
            name.includes("male") ||
            name.includes("man") ||
            name.includes("boy")
          );
        }
      });
      if (genderMatch) return genderMatch;
    }

    return null;
  };

  /**
   * Create and configure speech utterance
   */
  const createUtterance = (message: TTSMessage): SpeechSynthesisUtterance => {
    const utterance = new SpeechSynthesisUtterance(message.text);

    // Apply intonation settings
    const intonation = { ...defaultConfig.intonation, ...message.intonation };
    utterance.rate = intonation.rate ?? 1;
    utterance.pitch = intonation.pitch ?? 1;
    utterance.volume = intonation.volume ?? 1;

    // Apply voice settings
    const voiceConfig = { ...defaultConfig.voice, ...message.voice };
    const selectedVoice = findVoice(voiceConfig);
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }

    // Add event listeners
    utterance.onstart = () => {
      console.log("🎤 TTS: Speech started");
      if (!isDestroyed) {
        defaultConfig.onStart?.();
      }
    };

    utterance.onend = () => {
      console.log("✅ TTS: Speech ended");
      currentUtterance = null;
      if (!isDestroyed) {
        defaultConfig.onEnd?.();
      }
    };

    utterance.onerror = (event) => {
      console.error("❌ TTS: Speech error:", event.error);
      currentUtterance = null;
      if (!isDestroyed) {
        defaultConfig.onError?.(event.error);
      }
    };

    return utterance;
  };

  /**
   * Speak a message
   */
  const speak = (message: TTSMessage | string): void => {
    if (isDestroyed) return;

    // Stop any current speech
    stop();

    // Normalize message to TTSMessage object
    const ttsMessage: TTSMessage =
      typeof message === "string" ? { text: message } : message;

    if (!ttsMessage.text.trim()) return;

    try {
      currentUtterance = createUtterance(ttsMessage);
      speechSynthesis.speak(currentUtterance);
    } catch (error) {
      console.error("TTS speak error:", error);
      defaultConfig.onError?.(`Failed to speak: ${error}`);
    }
  };

  /**
   * Stop current speech
   */
  const stop = (): void => {
    if (isDestroyed) return;

    speechSynthesis.cancel();
    currentUtterance = null;
  };

  /**
   * Pause current speech
   */
  const pause = (): void => {
    if (isDestroyed) return;
    speechSynthesis.pause();
  };

  /**
   * Resume paused speech
   */
  const resume = (): void => {
    if (isDestroyed) return;
    speechSynthesis.resume();
  };

  /**
   * Check if currently speaking
   */
  const isSpeaking = (): boolean => {
    return speechSynthesis.speaking;
  };

  /**
   * Check if speech is paused
   */
  const isPaused = (): boolean => {
    return speechSynthesis.paused;
  };

  /**
   * Get available voices
   */
  const getVoices = (): SpeechSynthesisVoice[] => {
    return speechSynthesis.getVoices();
  };

  /**
   * Update default configuration
   */
  const setDefaultConfig = (newConfig: TTSConfig): void => {
    if (isDestroyed) return;
    defaultConfig = { ...defaultConfig, ...newConfig };
  };

  /**
   * Clean up resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;
    stop();
    currentUtterance = null;
  };

  return {
    speak,
    stop,
    pause,
    resume,
    isSpeaking,
    isPaused,
    getVoices,
    setDefaultConfig,
    destroy,
  };
};

/**
 * Global TTS instance for app-wide usage
 */
let globalTTSInstance: TTSAPI | null = null;

/**
 * Get or create global TTS instance
 */
export const getGlobalTTS = (): TTSAPI => {
  if (!globalTTSInstance) {
    globalTTSInstance = createTTSAPI();
  }
  return globalTTSInstance;
};

/**
 * Test messages with different intonations
 */
export const TTS_TEST_MESSAGES = {
  greeting: {
    text: "Hello! This is a test of the text-to-speech system.",
    intonation: { rate: 1, pitch: 1.2, volume: 0.8 },
  },
  excited: {
    text: "Great! The sketch recognition is working perfectly!",
    intonation: { rate: 1.06, pitch: 1.2, volume: 0.7 },
  },
  calm: {
    text: "Take your time. Draw whatever comes to mind.",
    intonation: { rate: 0.8, pitch: 0.9, volume: 0.7 },
  },
  processing: {
    text: "Processing your sketch... This might take a moment.",
    intonation: { rate: 0.9, pitch: 1, volume: 0.8 },
  },
  error: {
    text: "Sorry, I couldn't process that sketch. Please try again.",
    intonation: { rate: 0.8, pitch: 0.8, volume: 0.9 },
  },
} as const;
